/**
 * Privacy-Compliant Analytics Tracker
 * GDPR-compliant visitor tracking with consent management
 */

import React, { useEffect, useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface AnalyticsEvent {
  eventType: 'page_view' | 'click' | 'scroll' | 'conversion' | 'form_submit' | 'video_play';
  pageUrl: string;
  timestamp: number;
  eventData?: Record<string, any>;
  sessionId: string;
  visitorId: string;
}

interface ConsentStatus {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface PrivacyAnalyticsTrackerProps {
  enabled?: boolean;
  endpoint?: string;
  debugMode?: boolean;
  consentStatus?: ConsentStatus;
  visitorId?: string;
  testMode?: boolean;
}

class AnalyticsQueue {
  private queue: AnalyticsEvent[] = [];
  private maxQueueSize = 50;
  private flushInterval = 5000; // 5 seconds
  private timer: NodeJS.Timeout | null = null;

  constructor(private flushCallback: (events: AnalyticsEvent[]) => void) {
    this.startTimer();
  }

  add(event: AnalyticsEvent) {
    this.queue.push(event);

    if (this.queue.length >= this.maxQueueSize) {
      this.flush();
    }
  }

  flush() {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];
    this.flushCallback(events);
  }

  private startTimer() {
    this.timer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  destroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.flush();
  }
}

export const PrivacyAnalyticsTracker: React.FC<PrivacyAnalyticsTrackerProps> = ({
  enabled = true,
  endpoint = '/api/v1/analytics/events/privacy',
  debugMode = false,
  consentStatus = { necessary: true, analytics: false, marketing: false },
  visitorId: providedVisitorId,
  testMode = false,
}) => {
  const [sessionId, setSessionId] = useState<string>('');
  const [visitorId, setVisitorId] = useState<string>('');
  const [analyticsQueue, setAnalyticsQueue] = useState<AnalyticsQueue | null>(null);

  // Initialize visitor and session IDs
  useEffect(() => {
    // Get or create visitor ID
    const storedVisitorId = sessionStorage.getItem('analytics_visitor_id');
    const vid = providedVisitorId || storedVisitorId || generateAnonymousId();

    if (!storedVisitorId && !providedVisitorId) {
      sessionStorage.setItem('analytics_visitor_id', vid);
    }
    setVisitorId(vid);

    // Create session ID
    const sid = `sess_${generateAnonymousId()}`;
    setSessionId(sid);

    // Analytics session initialized in debug mode
  }, [providedVisitorId, debugMode, consentStatus]);

  // Generate anonymous ID that doesn't contain PII
  const generateAnonymousId = useCallback(() => {
    return uuidv4().replace(/-/g, '');
  }, []);

  // Send events to backend
  const sendEvents = useCallback(
    async (events: AnalyticsEvent[]) => {
      if (!enabled || !consentStatus.analytics) {
        if (debugMode) {
        }
        return;
      }

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Analytics-Version': '1.0.0',
          },
          body: JSON.stringify({
            events,
            metadata: {
              timestamp: Date.now(),
              pageUrl: window.location.href,
              referrer: document.referrer,
              userAgent: navigator.userAgent,
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              language: navigator.language,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          }),
        });

        if (!response.ok && debugMode) {
          console.error('[Analytics] Failed to send events:', response.status);
        }
      } catch (error) {
        if (debugMode) {
          console.error('[Analytics] Error sending events:', error);
        }
      }
    },
    [enabled, consentStatus.analytics, endpoint, debugMode]
  );

  // Initialize analytics queue
  useEffect(() => {
    if (!enabled || !consentStatus.analytics) {
      return;
    }

    const queue = new AnalyticsQueue(sendEvents);
    setAnalyticsQueue(queue);

    return () => {
      queue.destroy();
    };
  }, [enabled, consentStatus.analytics, sendEvents]);

  // Track page view
  const trackPageView = useCallback(
    (url?: string) => {
      if (!analyticsQueue || !consentStatus.analytics) return;

      const event: AnalyticsEvent = {
        eventType: 'page_view',
        pageUrl: url || window.location.href,
        timestamp: Date.now(),
        sessionId,
        visitorId,
        eventData: {
          title: document.title,
          path: window.location.pathname,
          hash: window.location.hash,
          search: window.location.search,
        },
      };

      analyticsQueue.add(event);

      if (debugMode) {
      }
    },
    [analyticsQueue, consentStatus.analytics, sessionId, visitorId, debugMode]
  );

  // Track custom events
  const trackEvent = useCallback(
    (eventType: AnalyticsEvent['eventType'], eventData?: Record<string, any>) => {
      if (!analyticsQueue || !consentStatus.analytics) return;

      const event: AnalyticsEvent = {
        eventType,
        pageUrl: window.location.href,
        timestamp: Date.now(),
        sessionId,
        visitorId,
        eventData,
      };

      analyticsQueue.add(event);

      if (debugMode) {
      }
    },
    [analyticsQueue, consentStatus.analytics, sessionId, visitorId, debugMode]
  );

  // Track conversion events (requires marketing consent)
  const trackConversion = useCallback(
    (conversionType: string, value?: number, currency?: string) => {
      if (!analyticsQueue || !consentStatus.marketing) {
        if (debugMode) {
        }
        return;
      }

      const event: AnalyticsEvent = {
        eventType: 'conversion',
        pageUrl: window.location.href,
        timestamp: Date.now(),
        sessionId,
        visitorId,
        eventData: {
          conversionType,
          value,
          currency,
        },
      };

      analyticsQueue.add(event);

      if (debugMode) {
      }
    },
    [analyticsQueue, consentStatus.marketing, sessionId, visitorId, debugMode]
  );

  // Auto-track page views on route changes
  useEffect(() => {
    if (!enabled || !consentStatus.analytics) return;

    // Track initial page view
    trackPageView();

    // Listen for route changes (for single-page apps)
    const handleRouteChange = () => {
      trackPageView();
    };

    window.addEventListener('popstate', handleRouteChange);

    // Also track when visibility changes (tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        trackEvent('page_view', { returnVisit: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, consentStatus.analytics, trackPageView, trackEvent]);

  // Track scroll depth
  useEffect(() => {
    if (!enabled || !consentStatus.analytics) return;

    let maxScrollDepth = 0;
    let scrollTimer: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollDepth = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollDepth > maxScrollDepth) {
        maxScrollDepth = scrollDepth;

        // Debounce scroll tracking
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          if (maxScrollDepth >= 25 && maxScrollDepth < 50) {
            trackEvent('scroll', { depth: 25 });
          } else if (maxScrollDepth >= 50 && maxScrollDepth < 75) {
            trackEvent('scroll', { depth: 50 });
          } else if (maxScrollDepth >= 75 && maxScrollDepth < 90) {
            trackEvent('scroll', { depth: 75 });
          } else if (maxScrollDepth >= 90) {
            trackEvent('scroll', { depth: 90 });
          }
        }, 1000);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, [enabled, consentStatus.analytics, trackEvent]);

  // Expose tracking methods via window for manual tracking
  useEffect(() => {
    if (!enabled) return;

    const analytics = {
      trackPageView,
      trackEvent,
      trackConversion,
      isEnabled: () => consentStatus.analytics,
      getVisitorId: () => visitorId,
      getSessionId: () => sessionId,
    };

    (window as any).privacyAnalytics = analytics;

    return () => {
      delete (window as any).privacyAnalytics;
    };
  }, [enabled, trackPageView, trackEvent, trackConversion, consentStatus.analytics, visitorId, sessionId]);

  // Component doesn't render anything
  return null;
};

export default PrivacyAnalyticsTracker;
